import React, { useState } from 'react';
import { config } from '../../config';
import './scss/TextToTasksParser.scss';
import Close from '../icons/Close';

const TextToTasksParser = ({ boardId, boardTitle, onTasksCreated, onClose }) => {
  const [inputText, setInputText] = useState('');
  const [parsedTasks, setParsedTasks] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('input'); // 'input', 'review', 'creating'

  const exampleTexts = [
    `Meeting Notes - Sprint Planning:
- Create user authentication system
- Design responsive navigation component  
- Set up database migration scripts
- Write unit tests for core functionality
- Review and update API documentation`,
    
    `Project Tasks:
1. Implement user dashboard with analytics
2. Fix bug in payment processing module
3. Add dark mode support to the application
4. Optimize database queries for better performance
5. Create onboarding flow for new users`,
    
    `Action Items from Client Call:
• Update homepage design by Friday
• Research competitor pricing models
• Prepare demo for next week's presentation
• Fix mobile responsiveness issues
• Schedule follow-up meeting with stakeholders`
  ];

  const handleParseText = async () => {
    if (!inputText.trim()) return;

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${config.API_URI}/v1/ai/nlp/parse-tasks`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: inputText,
          boardId,
          boardTitle
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to parse text');
      }

      const data = await response.json();
      if (data.success) {
        setParsedTasks(data.tasks);
        setSelectedTasks(new Set(data.tasks.map((_, index) => index)));
        setStep('review');
      } else {
        throw new Error(data.error || 'Failed to parse text to tasks');
      }
    } catch (err) {
      console.error('Text parsing error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskSelection = (index) => {
    const newSelected = new Set(selectedTasks);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedTasks(newSelected);
  };

  const handleCreateTasks = async () => {
    const tasksToCreate = parsedTasks.filter((_, index) => selectedTasks.has(index));
    
    if (tasksToCreate.length === 0) return;

    setStep('creating');
    
    // Simulate task creation (you'll need to implement actual task creation)
    try {
      // This would typically call your card creation API
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      if (onTasksCreated) {
        onTasksCreated(tasksToCreate);
      }
      
      onClose?.();
    } catch (err) {
      setError('Failed to create tasks');
      setStep('review');
    }
  };

  const handleUseExample = (exampleText) => {
    setInputText(exampleText);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'bug': return 'bug_report';
      case 'feature': return 'new_releases';
      case 'documentation': return 'description';
      case 'testing': return 'verified';
      case 'design': return 'design_services';
      case 'backend': return 'dns';
      case 'frontend': return 'web';
      default: return 'task';
    }
  };

  if (step === 'creating') {
    return (
      <div className="text-parser-modal">
        <div className="parser-content modern-card">
          <div className="creating-tasks">
            <div className="loading-animation">
              <div className="ai-icon-pulse">
                <span className="material-symbols-outlined">auto_awesome</span>
              </div>
              <div className="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
            <h3>Creating Tasks...</h3>
            <p>Please wait while we create {selectedTasks.size} tasks for you.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="text-parser-modal">
      <div className="parser-content modern-card">
        <div className="parser-header">
          <div className="header-content">
            <div className="ai-avatar">
              <span className="material-symbols-outlined">auto_awesome</span>
            </div>
            <div className="header-text">
              <h2>Convert Text to Tasks</h2>
              <p>Paste your meeting notes, action items, or task lists and let AI convert them to structured tasks</p>
            </div>
          </div>
          <button onClick={onClose} className="action-btn close-btn" title="Close">
            <Close size={24} />
          </button>
        </div>

        {step === 'input' && (
          <div className="input-step">
            <div className="text-input-section">
              <div className="input-container modern-input">
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Paste your meeting notes, action items, or task lists here..."
                  className="text-input"
                  rows={12}
                />
              </div>

              <div className="input-actions">
                <button
                  onClick={handleParseText}
                  disabled={loading || !inputText.trim()}
                  className="parse-btn"
                >
                  {loading ? (
                    <>
                      <div className="btn-spinner">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                      Parsing...
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined">psychology</span>
                      Parse to Tasks
                    </>
                  )}
                </button>
              </div>

              {error && (
                <div className="parse-error modern-error">
                  <span className="material-symbols-outlined">error_outline</span>
                  <span>{error}</span>
                </div>
              )}
            </div>

            <div className="examples-section modern-examples">
              <div className="examples-header">
                <h4>
                  <span className="material-symbols-outlined">lightbulb</span>
                  Try these examples:
                </h4>
              </div>
              <div className="examples-list">
                {exampleTexts.map((example, index) => (
                  <div key={index} className="example-item">
                    <div className="example-preview">
                      <span className="example-title">{example.split('\n')[0]}</span>
                      <span className="example-snippet">...</span>
                    </div>
                    <button
                      onClick={() => handleUseExample(example)}
                      className="use-example-btn"
                    >
                      <span className="material-symbols-outlined">content_copy</span>
                      Use Example
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 'review' && (
          <div className="review-step">
            <div className="review-header">
              <h4>
                <span className="material-symbols-outlined">checklist</span>
                Review Parsed Tasks ({parsedTasks.length} found)
              </h4>
              <p>Select the tasks you want to create. You can edit them after creation.</p>
            </div>

            <div className="tasks-list modern-tasks">
              {parsedTasks.map((task, index) => (
                <div
                  key={index}
                  className={`task-item modern-task ${selectedTasks.has(index) ? 'selected' : ''}`}
                >
                  <div className="task-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedTasks.has(index)}
                      onChange={() => handleTaskSelection(index)}
                      id={`task-${index}`}
                    />
                    <label htmlFor={`task-${index}`} className="checkbox-label">
                      <span className="checkmark">
                        <span className="material-symbols-outlined">check</span>
                      </span>
                    </label>
                  </div>

                  <div className="task-content">
                    <div className="task-header">
                      <div className="task-title">{task.title}</div>
                      <div className="task-meta">
                        {task.category && (
                          <span className="task-category">
                            <span className="material-symbols-outlined">
                              {getCategoryIcon(task.category)}
                            </span>
                            <span>{task.category}</span>
                          </span>
                        )}
                        {task.priority && (
                          <span
                            className={`task-priority priority-${task.priority}`}
                          >
                            <span className="priority-dot"></span>
                            <span>{task.priority} priority</span>
                          </span>
                        )}
                        {task.estimatedHours && (
                          <span className="task-estimate">
                            <span className="material-symbols-outlined">schedule</span>
                            <span>{task.estimatedHours}h</span>
                          </span>
                        )}
                      </div>
                    </div>

                    {task.description && (
                      <div className="task-description">{task.description}</div>
                    )}

                    {task.labels && task.labels.length > 0 && (
                      <div className="task-labels">
                        {task.labels.map((label, labelIndex) => (
                          <span key={labelIndex} className="task-label">
                            #{label}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="task-confidence">
                    <span className={`confidence-badge ${task.confidence > 0.8 ? 'high' : task.confidence > 0.6 ? 'medium' : 'low'}`}>
                      <span className="material-symbols-outlined">verified</span>
                      <span>{Math.round(task.confidence * 100)}%</span>
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="review-actions modern-actions">
              <div className="selection-info">
                <div className="selection-count">
                  <span className="count-badge">{selectedTasks.size}</span>
                  <span>of {parsedTasks.length} tasks selected</span>
                </div>
              </div>

              <div className="action-buttons">
                <button
                  onClick={() => setStep('input')}
                  className="back-btn"
                >
                  <span className="material-symbols-outlined">arrow_back</span>
                  Back to Edit
                </button>
                <button
                  onClick={handleCreateTasks}
                  disabled={selectedTasks.size === 0}
                  className="create-btn"
                >
                  <span className="material-symbols-outlined">add_task</span>
                  Create {selectedTasks.size} Tasks
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="parser-footer">
          <div className="ai-branding">
            <div className="ai-icon">
              <span className="material-symbols-outlined">psychology</span>
            </div>
            <div className="ai-text">
              <span className="ai-label">Powered by AI</span>
              <span className="ai-description">AI parsing may not be perfect. Please review and edit tasks as needed.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TextToTasksParser;
